/**
 * Benefits grid – Web Component
 * Mobile: Swiper slider, centered slides.
 * Desktop: grid layout, Swiper destroyed.
 */
if (!customElements.get('benefits-grid-slider')) {
  class BenefitsGridSlider extends HTMLElement {
    static get BREAKPOINT() {
      return 590;
    }

    static get MARQUEE_REPLACEMENT_BREAKPOINT() {
      return 1340;
    }

    constructor() {
      super();
      this.swiper = null;
      this._resizeBound = null;
      this._resizeTimer = null;
      this._autoplayTimer = null;
      this._swiperRetryTimer = null;
    }

    connectedCallback() {
      this._resizeBound = this._onResize.bind(this);
      this._swiperLoadedBound = this.update.bind(this);
      this.update();
      window.addEventListener('resize', this._resizeBound);
      document.addEventListener('swiper:loaded', this._swiperLoadedBound);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._resizeBound);
      clearTimeout(this._swiperRetryTimer);
      this._destroySwiper();
    }

    _sliderBreakpoint() {
      return this.dataset.mobileMarquee === 'true'
        ? BenefitsGridSlider.MARQUEE_REPLACEMENT_BREAKPOINT
        : BenefitsGridSlider.BREAKPOINT;
    }

    _isMobile() {
      return window.innerWidth < this._sliderBreakpoint();
    }

    update() {
      if (this._isMobile()) {
        this._initSwiper();
      } else {
        this._destroySwiper();
      }
    }

    _initSwiper() {
      if (this.swiper || !this._isMobile()) return;

      if (!window.Swiper) {
        clearTimeout(this._swiperRetryTimer);
        this._swiperRetryTimer = setTimeout(() => this._initSwiper(), 150);
        return;
      }

      const list = this.querySelector('.benefits-grid__list');
      if (!list) return;
      clearTimeout(this._swiperRetryTimer);

      const originalSlides = Array.from(list.querySelectorAll('.swiper-slide:not([data-benefits-grid-clone])'));
      const originalSlideCount = originalSlides.length;
      const raw = parseInt(this.getAttribute('data-initial-slide'), 10);
      const initialSlide = !isNaN(raw) ? Math.max(0, Math.min(raw, originalSlideCount - 1)) : 0;
      const useAutoplay = this.dataset.mobileMarquee === 'true' && originalSlideCount > 1;

      if (useAutoplay) {
        this._addLoopClones(list, originalSlides);
      }

      const slideCount = list.querySelectorAll('.swiper-slide').length;
      this.swiper = new window.Swiper(this, {
        initialSlide,
        slidesPerView: 'auto',
        centeredSlides: true,
        spaceBetween: 13,
        loop: slideCount > 1,
        slidesPerGroup: 1,
        autoplay: false,
        speed: 500,
        threshold: 6,
        on: {
          init: () => {
            this.classList.add('benefits-grid__slider--active');
            if (useAutoplay) this._startAutoplay();
          },
          destroy: () => this.classList.remove('benefits-grid__slider--active')
        }
      });
    }

    _addLoopClones(list, slides) {
      if (list.querySelector('[data-benefits-grid-clone]')) return;

      for (let repeat = 0; repeat < 2; repeat += 1) {
        slides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.setAttribute('data-benefits-grid-clone', 'true');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('id');
          list.appendChild(clone);
        });
      }
    }

    _removeLoopClones() {
      this.querySelectorAll('[data-benefits-grid-clone]').forEach((clone) => clone.remove());
    }

    _startAutoplay() {
      this._stopAutoplay();
      this._autoplayTimer = setInterval(() => {
        if (!this.swiper || this.swiper.destroyed) return;
        this.swiper.slideNext(500);
      }, 7000);
    }

    _stopAutoplay() {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }

    _destroySwiper() {
      this._stopAutoplay();
      if (!this.swiper) {
        this._removeLoopClones();
        return;
      }
      this.swiper.destroy(true, true);
      this.swiper = null;
      this._removeLoopClones();
      this.classList.remove('benefits-grid__slider--active');
    }

    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.update(), 150);
    }
  }

  customElements.define('benefits-grid-slider', BenefitsGridSlider);
}
